import React, { useState } from "react";
import { 
    Box, Typography, TextField, Button, Paper, Avatar, IconButton, 
    Select, MenuItem, FormControl, Checkbox, FormControlLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, FormLabel, Alert, Snackbar, CircularProgress
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useLocation } from "react-router-dom";
import { searchCandidates, searchAllCandidates, downloadCandidateResume, getCandidateResume, createInterview } from "../../Api/CandidateApi";
import { getAllSkills, searchSkillsApi } from "../../Api/SkillsApi";
import { getJobs } from "../../Api/JobsServiceApi";

const BRAND = '#6B31C4';
const BRAND_DARK = '#5a29a5';

const DURATION_OPTIONS = [
    { label: '15 minutes', value: 15 },
    { label: '30 minutes', value: 30 },
    { label: '45 minutes', value: 45 },
    { label: '1 hour', value: 60 },
    { label: '1.5 hours', value: 90 },
    { label: '2 hours', value: 120 },
];
const HOURS_12 = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const MINUTES = ['00', '15', '30', '45'];
const TIMEZONES = ['Asia/Kolkata', 'UTC', 'America/New_York', 'Europe/London'];

const getInitialDateTime = () => {
    const now = new Date();
    const defaultTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour in future
    const yyyy = defaultTime.getFullYear();
    const mm = String(defaultTime.getMonth() + 1).padStart(2, '0');
    const dd = String(defaultTime.getDate()).padStart(2, '0');
    
    let rawHour = defaultTime.getHours();
    const period = rawHour >= 12 ? 'PM' : 'AM';
    let hour12 = rawHour % 12;
    if (hour12 === 0) hour12 = 12;
    
    return {
        date: `${yyyy}-${mm}-${dd}`,
        hour: String(hour12).padStart(2, '0'),
        minute: '00',
        period: period
    };
};

const SearchResults = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [filters, setFilters] = useState({
        timeAtJob: "Show All",
        availableImmediately: false,
        skills: [],
        jobTitle: [],
        location: "",
    });

    // Update filter parameters directly in the URL query string to avoid race conditions and infinite render loops
    const handleFilterChange = (updatedFilters) => {
        const searchParams = new URLSearchParams(location.search);
        
        // experience
        if (updatedFilters.timeAtJob && updatedFilters.timeAtJob !== "Show All") {
            searchParams.set("experience", updatedFilters.timeAtJob);
        } else {
            searchParams.delete("experience");
        }

        // noticePeriod
        if (updatedFilters.availableImmediately) {
            searchParams.set("noticePeriod", "Immediate");
        } else {
            searchParams.delete("noticePeriod");
        }

        // skills
        if (updatedFilters.skills && updatedFilters.skills.length > 0) {
            searchParams.set("skills", updatedFilters.skills.join(","));
        } else {
            searchParams.delete("skills");
        }

        // location
        if (updatedFilters.location && updatedFilters.location.trim() !== "") {
            searchParams.set("location", updatedFilters.location.trim());
        } else {
            searchParams.delete("location");
        }

        // jobTitle
        if (updatedFilters.jobTitle && updatedFilters.jobTitle.length > 0) {
            searchParams.set("jobTitle", updatedFilters.jobTitle.join(","));
        } else {
            searchParams.delete("jobTitle");
        }

        searchParams.delete("page"); // Reset page on filter change
        navigate(`${location.pathname}?${searchParams.toString()}`);
    };

    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    React.useEffect(() => {
        const fetchCandidates = async () => {
            setLoading(true);
            try {
                // Parse URL search parameters to initialize frontend filters
                const searchParams = new URLSearchParams(location.search);
                const urlSkills = searchParams.get("skills");
                const urlTitle = searchParams.get("jobTitle") || searchParams.get("title");
                const urlSearch = searchParams.get("search") || searchParams.get("query");
                const urlExperience = searchParams.get("experience") || searchParams.get("jobExperience");
                const urlNoticePeriod = searchParams.get("noticePeriod");
                const urlLocation = searchParams.get("location");

                // Map experience query parameter to internal state timeAtJob
                let timeAtJobVal = "Show All";
                if (urlExperience) {
                    const expStr = urlExperience.toLowerCase();
                    if (expStr.includes("less than 1") || expStr === "0") {
                        timeAtJobVal = "Less than 1 year";
                    } else if (expStr.includes("1-2") || expStr.includes("1 year") || expStr.includes("2 year")) {
                        timeAtJobVal = "1-2 years";
                    } else if (expStr.includes("2-5")) {
                        timeAtJobVal = "2-5 years";
                    } else if (expStr.includes("5-10") || expStr.includes("5 year")) {
                        timeAtJobVal = "5-10 years";
                    } else if (expStr.includes("10+") || expStr.includes("10 year") || expStr.includes("5+")) {
                        timeAtJobVal = "10+ years";
                    }
                }

                // Map noticePeriod query parameter to internal availability state
                let availableImmediatelyVal = false;
                if (urlNoticePeriod) {
                    const npStr = urlNoticePeriod.toLowerCase();
                    if (npStr.includes("immediate") || npStr.includes("0")) {
                        availableImmediatelyVal = true;
                    }
                }

                setFilters({
                    timeAtJob: timeAtJobVal,
                    availableImmediately: availableImmediatelyVal,
                    skills: (urlSkills && urlSkills !== "") ? urlSkills.split(",") : [],
                    jobTitle: (urlTitle && urlTitle !== "") ? urlTitle.split(",") : [],
                    location: urlLocation || "",
                });

                setSearchQuery(urlTitle || urlSearch || "");

                const data = await searchAllCandidates(location.search);
                const rawList = data?.jobApplications || data?.data || [];
                
                // Map raw items into flattened candidate records
                const mappedCandidates = rawList.map(item => {
                    const isApplication = !!item.candidate;
                    const candidate = isApplication ? item.candidate : item;
                    
                    return {
                        id: item.id,
                        userId: isApplication ? item.userId : item.id,
                        name: candidate?.name || '',
                        email: candidate?.email || '',
                        avatarUrl: candidate?.personalDetails?.profile || candidate?.avatarUrl || '',
                        location: candidate?.preferredLocations?.[0]?.location?.name || '',
                        skills: candidate?.userSkills?.map(us => us.skill) || candidate?.skills || [],
                        experience: candidate?.personalDetails?.experience || candidate?.experience || null,
                        phone: candidate?.mobile || candidate?.personalDetails?.phone || candidate?.phone || '',
                        noticePeriod: candidate?.personalDetails?.noticePeriod || candidate?.noticePeriod || '',
                        jobTitle: isApplication ? item.job?.title : (candidate?.personalDetails?.designation || candidate?.personalDetails?.jobTitle || ''),
                        status: isApplication ? item.currentStatus : 'pending',
                        appliedAt: isApplication ? (item.appliedAt || item.createdAt) : candidate.createdAt,
                        _raw: item,
                    };
                });

                setCandidates(mappedCandidates);
                setCurrentPage(data?.page || 1);
                setTotalPages(data?.totalPages || 1);

                if (mappedCandidates.length > 0) {
                    setSelectedCandidate(mappedCandidates[0]);
                } else {
                    setSelectedCandidate(null);
                }
            } catch (err) {
                console.error("Error fetching candidates:", err);
                setCandidates([]);
                setSelectedCandidate(null);
            } finally {
                setLoading(false);
            }
        };

        fetchCandidates();
    }, [location.search]);

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("page", newPage);
        navigate(`${location.pathname}?${searchParams.toString()}`);
    };

    const [skillOptions, setSkillOptions] = useState([]);
    const [jobTitleOptions, setJobTitleOptions] = useState([]);
    const [skillsSearchQuery, setSkillsSearchQuery] = useState("");
    const [searchedSkills, setSearchedSkills] = useState([]);

    // Debounced search for skills in the sidebar filter
    React.useEffect(() => {
        if (!skillsSearchQuery.trim()) {
            setSearchedSkills([]);
            return;
        }
        const delayDebounce = setTimeout(async () => {
            try {
                const res = await searchSkillsApi(skillsSearchQuery);
                if (res?.success && res?.data) {
                    setSearchedSkills(res.data.map(s => s.name || s.skillName || s));
                }
            } catch (err) {
                console.error("Failed to search skills in sidebar:", err);
            }
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [skillsSearchQuery]);

    React.useEffect(() => {
        const fetchFilters = async () => {
            try {
                // Fetch popular skills from API (fetch 100)
                const skillsRes = await getAllSkills(100);
                if (skillsRes?.success && skillsRes?.data) {
                    setSkillOptions(skillsRes.data.map(s => s.name || s.skillName || s).sort());
                }

                // Fetch jobs to get job titles
                const jobsRes = await getJobs();
                if (jobsRes?.success && jobsRes?.jobs) {
                    setJobsList(jobsRes.jobs);
                    const titlesSet = new Set(jobsRes.jobs.map(j => j.title).filter(Boolean));
                    setJobTitleOptions(Array.from(titlesSet).sort());
                }
            } catch (err) {
                console.error("Error fetching filter options:", err);
            }
        };
        fetchFilters();
    }, []);


    const [jobsList, setJobsList] = useState([]);
    const initialDt = getInitialDateTime();
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [scheduleForm, setScheduleForm] = useState({
        jobId: '', date: initialDt.date, hour: initialDt.hour, minute: initialDt.minute, period: initialDt.period, durationMinutes: 30, message: '', mode: 'online', timeZone: 'Asia/Kolkata'
    });
    const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
    const [scheduleError, setScheduleError] = useState('');
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    const handleDownload = (userId) => {
        if (!userId) return;
        downloadCandidateResume(userId);
    };

    const handleViewResume = async (cand) => {
        try {
            const res = await getCandidateResume(cand.userId || cand.id);
            if (res?.success && res?.data?.resumeUrl) {
                window.open(res.data.resumeUrl, '_blank');
            } else {
                setSnack({ open: true, msg: "No resume available for this candidate.", severity: "error" });
            }
        } catch (e) {
            setSnack({ open: true, msg: "Failed to fetch candidate resume.", severity: "error" });
        }
    };

    const handleOpenSchedule = (cand) => {
        const dt = getInitialDateTime();
        const candJobId = cand?._raw?.jobId || cand?._raw?.job?.id;
        setScheduleForm({
            jobId: candJobId || (jobsList.length > 0 ? jobsList[0].id : ''),
            date: dt.date,
            hour: dt.hour,
            minute: dt.minute,
            period: dt.period,
            durationMinutes: 30,
            message: `Hello ${cand.name}, we would like to schedule an interview with you.`,
            mode: 'online',
            timeZone: 'Asia/Kolkata'
        });
        setScheduleError('');
        setScheduleOpen(true);
    };

    const handleScheduleSubmit = async () => {
        const finalJobId = scheduleForm.jobId || selectedCandidate?._raw?.jobId || selectedCandidate?._raw?.job?.id;
        if (!finalJobId) {
            setScheduleError('Please select a Job Role.');
            return;
        }
        if (!scheduleForm.date || !scheduleForm.message) {
            setScheduleError('Date and message are required.');
            return;
        }
        
        const hourVal = parseInt(scheduleForm.hour, 10);
        let hour24 = hourVal;
        if (scheduleForm.period === 'PM' && hourVal !== 12) {
            hour24 += 12;
        } else if (scheduleForm.period === 'AM' && hourVal === 12) {
            hour24 = 0;
        }
        const hourStr = String(hour24).padStart(2, '0');

        const selectedStart = new Date(`${scheduleForm.date}T${hourStr}:${scheduleForm.minute}:00`);
        const now = new Date();
        selectedStart.setSeconds(0, 0);
        now.setSeconds(0, 0);
        if (selectedStart < now) {
            setScheduleError('Cannot schedule interviews in the past.');
            return;
        }

        setScheduleSubmitting(true);
        setScheduleError('');
        try {
            const startAt = selectedStart.toISOString();
            
            // Decode the JWT token to fetch employer ID dynamically
            let employerId = localStorage.getItem('employerId');
            if (!employerId) {
                const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                if (token) {
                    try {
                        const base64Url = token.split('.')[1];
                        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                        const jsonPayload = decodeURIComponent(
                            window.atob(base64)
                                .split('')
                                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                                .join('')
                        );
                        employerId = JSON.parse(jsonPayload).id;
                    } catch (e) {
                        console.error("Token decode error:", e);
                    }
                }
            }

            await createInterview({
                jobId: Number(finalJobId),
                applicationId: selectedCandidate?._raw?.id || null,
                candidateId: selectedCandidate?.userId || selectedCandidate?._raw?.userId,
                createdByEmployerId: employerId ? Number(employerId) : null,
                interviewStartAt: startAt,
                status: 'scheduled',
                mode: scheduleForm.mode,
                timeZone: scheduleForm.timeZone,
                durationMinutes: Number(scheduleForm.durationMinutes),
                message: scheduleForm.message
            });

            setSnack({ open: true, msg: 'Interview scheduled successfully!', severity: 'success' });
            setScheduleOpen(false);
        } catch (err) {
            setScheduleError(err?.response?.data?.message || 'Failed to schedule interview. Please try again.');
        } finally {
            setScheduleSubmitting(false);
        }
    };

    const handleBack = () => {
        navigate("/search-candidate");
    };

    const handleCandidateSelect = (candidate) => {
        setSelectedCandidate(candidate);
    };

    const handleSaveFilters = (newFilters) => {
        handleFilterChange(newFilters);
    };

    const handleSearchSubmit = () => {
        const searchParams = new URLSearchParams(location.search);
        if (searchQuery.trim()) {
            searchParams.set("jobTitle", searchQuery.trim());
            searchParams.delete("search");
            searchParams.delete("query");
        } else {
            searchParams.delete("jobTitle");
            searchParams.delete("search");
            searchParams.delete("query");
        }
        searchParams.delete("page");
        navigate(`${location.pathname}?${searchParams.toString()}`);
    };

    const filteredCandidates = candidates;

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#F8F9FA' }}>
            {/* Top Header with Back Arrow */}
            <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton onClick={handleBack} sx={{ p: 0.5 }}>
                        <ArrowBackIcon sx={{ fontSize: 22, color: '#6B7280' }} />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A2E', fontSize: '1rem' }}>
                        Search: {searchQuery}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "stretch", gap: 2, px: 3, pb: 2, flex: 1, minHeight: 0, overflow: "hidden" }}>
                {/* Left Sidebar - Filters */}
                <Box
                    sx={{
                        width: "230px",
                        flexShrink: 0,
                        overflow: "hidden",
                    }}
                >
                    <Paper elevation={0}
                        sx={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: '12px',
                            p: 1.5,
                            border: '1px solid #E5E7EB',
                        }} >
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A2E', fontSize: '0.9375rem', mb: 2 }}>
                            Filters
                        </Typography>

                        {/* Time at current job */}
                        <Box sx={{ mb: 1 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 500,
                                    color: "#333",
                                    fontSize: "14px",
                                    mb: 1,
                                }}
                            >
                                Time at current job
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={filters.timeAtJob}
                                    onChange={(e) =>
                                        handleFilterChange({ ...filters, timeAtJob: e.target.value })
                                    }
                                    sx={{
                                        borderRadius: 1,
                                        fontSize: "14px",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#E0E0E0",
                                        },
                                    }}
                                >
                                    <MenuItem value="Show All">Show All</MenuItem>
                                    <MenuItem value="Less than 1 year">Less than 1 year</MenuItem>
                                    <MenuItem value="1-2 years">1-2 years</MenuItem>
                                    <MenuItem value="2-5 years">2-5 years</MenuItem>
                                    <MenuItem value="5-10 years">5-10 years</MenuItem>
                                    <MenuItem value="10+ years">10+ years</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
 
                        {/* Availability */}
                        <Box >
                            <Typography variant="subtitle2"
                                sx={{ fontWeight: 500, color: "#333", }}   >
                                Availability
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={filters.availableImmediately}
                                        onChange={(e) => handleFilterChange({ ...filters, availableImmediately: e.target.checked, })}
                                    // sx={{ color: "#A8A8AC" }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ color: "#333", }}>
                                        Available immediately
                                    </Typography>
                                }
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#333', fontSize: '14px', mb: 1 }}>
                                Location
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="City, State, or ZIP"
                                value={filters.location || ""}
                                onChange={(e) => handleFilterChange({ ...filters, location: e.target.value })}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px', backgroundColor: '#fcfdff' } }}
                            />
                        </Box>
 
                        {/* Skills */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2"
                                sx={{ fontWeight: 500, color: "#333", fontSize: "14px", mb: 0.5 }} >
                                Skills
                            </Typography>
                            <TextField
                                size="small"
                                placeholder="Search skills..."
                                value={skillsSearchQuery}
                                onChange={(e) => setSkillsSearchQuery(e.target.value)}
                                sx={{
                                    mb: 1.5,
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 1,
                                        fontSize: "13px",
                                        backgroundColor: "#fcfdff"
                                    }
                                }}
                            />
                            <Box sx={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                maxHeight: "200px", 
                                overflowY: "auto", 
                                gap: 0.5,
                                // Style standard scrollbar
                                "&::-webkit-scrollbar": {
                                    width: "6px"
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "#d1c4e9",
                                    borderRadius: "10px"
                                }
                            }}>
                                {Array.from(new Set([
                                    ...filters.skills,
                                    ...(skillsSearchQuery.trim() !== "" ? searchedSkills : skillOptions)
                                ])).map((skill, index) => (
                                    <FormControlLabel key={`${skill}-${index}`}
                                        control={
                                            <Checkbox
                                                checked={filters.skills.includes(skill)}
                                                onChange={(e) => {
                                                    const updatedSkills = e.target.checked
                                                        ? [...filters.skills, skill]
                                                        : filters.skills.filter((s) => s !== skill);
                                                    handleFilterChange({
                                                        ...filters,
                                                        skills: updatedSkills,
                                                    });
                                                }}
                                                sx={{ py: 0.5 }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ color: "#333", fontSize: "13px" }}>
                                                {skill}
                                            </Typography>
                                        }
                                        sx={{ margin: 0, alignItems: "center" }}
                                    />
                                ))}
                                {(skillsSearchQuery.trim() !== "" ? searchedSkills : skillOptions).length === 0 && (
                                    <Typography variant="caption" sx={{ color: "#999", fontStyle: "italic", p: 1 }}>
                                        No skills found
                                    </Typography>
                                )}
                            </Box>
                        </Box>
 
                        {/* Job Title */}
                        <Box>
                           
                            <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 0.5 }}>
                                {jobTitleOptions.map((title, index) => (
                                    <FormControlLabel key={`${title}-${index}`}
                                        control={
                                            <Checkbox checked={filters.jobTitle.includes(title)}
                                                onChange={(e) => {
                                                    const updatedTitles = e.target.checked
                                                        ? [...filters.jobTitle, title]
                                                        : filters.jobTitle.filter((t) => t !== title);
                                                    handleFilterChange({
                                                        ...filters,
                                                        jobTitle: updatedTitles,
                                                    });
                                                }}
                                                sx={{ color: "#A8A8AC" }}
                                            />
                                        }
                                        label={<Typography variant="body2" sx={{ color: "#333" }}> {title}</Typography>
                                        }
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Paper>
                </Box>

                {/* Main Content Area */}
                <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: '#F2F7FF', padding: '10px', borderRadius: '6px', overflow: "hidden" }}>
                    {/* Top Bar */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, }} >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}>
                            <TextField
                                placeholder="Search: UI Designer"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearchSubmit();
                                    }
                                }}
                                size="small"
                                sx={{
                                    width: "300px",
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2, backgroundColor: "#fff",
                                    },
                                }}
                            />
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleSearchSubmit}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress color="inherit" size={16} /> : null}
                                sx={{
                                    ml: 1,
                                    backgroundColor: "#6B31C4",
                                    color: "#fff",
                                    textTransform: "none",
                                    borderRadius: 2,
                                    px: 2,
                                    py: 0.75,
                                    '&.Mui-disabled': {
                                        backgroundColor: '#6B31C4',
                                        opacity: 0.7,
                                        color: '#fff',
                                    },
                                }}
                            >
                                {loading ? 'Searching...' : 'Search'}
                            </Button>
                            <Typography variant="body1" sx={{ color: "#666", fontSize: "14px" }}>
                                {filteredCandidates.length} Matches
                            </Typography>
                        </Box>
                        {/* Pagination */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton 
                                disableTouchRipple 
                                disableRipple 
                                size="small" 
                                sx={{ border: "1px solid #E0E0E0" }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage <= 1}
                            >
                                <ChevronLeftIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <Button 
                                    disableTouchRipple 
                                    disableRipple 
                                    key={page} 
                                    variant={page === currentPage ? "contained" : "text"}
                                    onClick={() => handlePageChange(page)}
                                    sx={{
                                        minWidth: "28px", height: "28px", p: 0,
                                        backgroundColor: page === currentPage ? "#6B31C4" : "transparent",
                                        color: page === currentPage ? "#fff" : "#6B31C4",
                                        "&:hover": {
                                            backgroundColor: page === currentPage ? "#6B31C4" : "rgba(0,0,0,0.04)",
                                        },
                                    }}
                                >
                                    {page}
                                </Button>
                            ))}
                            <IconButton 
                                disableTouchRipple 
                                disableRipple 
                                size="small" 
                                sx={{ border: "1px solid #E0E0E0" }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages}
                            >
                                <ChevronRightIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Content Area - Candidate List and Details */}
                    <Box sx={{ display: "flex", gap: 2, flexGrow: 1, minHeight: 0 }}>
                        {/* Candidate List */}
                        <Box sx={{ width: "300px", flexShrink: 0, minHeight: 0 }}>
                            <Paper elevation={0}
                                sx={{
                                    backgroundColor: "#fff",
                                    borderRadius: 2,
                                    border: "1px solid #E0E0E0",
                                    height: "100%",
                                    overflowY: "auto",
                                    // Hide scrollbar
                                    scrollbarWidth: "none", // Firefox
                                    "&::-webkit-scrollbar": {
                                        display: "none", // Chrome, Safari, Edge
                                    },
                                    msOverflowStyle: "none", // IE and Edge
                                }}
                            >
                                {filteredCandidates.map((candidate) => (
                                    <Box
                                        key={candidate.id}
                                        onClick={() => handleCandidateSelect(candidate)}
                                        sx={{
                                            p: 2,
                                            borderBottom: "1px solid #F0F0F0",
                                            cursor: "pointer",
                                            "&:hover": {
                                                backgroundColor: "#F9F9F9",
                                            },
                                            backgroundColor:
                                                selectedCandidate?.id === candidate.id ? "#F5F5F5" : "#fff",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                                            <Avatar
                                                src={candidate.avatarUrl || ""}
                                                sx={{
                                                    width: 42, height: 42,
                                                    backgroundColor: "#E0E0E0",
                                                }}
                                            >
                                                <PersonIcon sx={{ color: "#999" }} />
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: "#6B31C4",
                                                        fontSize: "16px",

                                                    }}
                                                >
                                                    {candidate.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: "#999", fontSize: "12px", mb: 1 }}
                                                >
                                                    {candidate.experience ? `${candidate.experience} years experience` : "Experience not specified"}
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                                    <LocationOnIcon sx={{ fontSize: 16, color: "#999" }} />
                                                    <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                                                        {candidate.location || "Location not specified"}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                                                    <PersonIcon sx={{ fontSize: 16, color: "#999" }} />
                                                    <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                                                        {candidate.jobTitle || "Job title not specified"}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        height: "4px",
                                                        backgroundColor: "#FFE0E6",
                                                        borderRadius: 2,
                                                        mb: 1,
                                                        position: "relative",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: `${candidate.match || 75}%`,

                                                            backgroundColor: "#FF6B9D",
                                                            borderRadius: 2,
                                                        }}
                                                    />
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: "#FF6B9D", fontSize: "12px", fontWeight: 600, mb: 1 }}
                                                >
                                                    {candidate.match || 75}% Match
                                                </Typography>
                                                <Box sx={{ display: "flex", gap: 1 }}>
                                                    <Button
                                                        variant="text"
                                                        onClick={(e) => { e.stopPropagation(); handleViewResume(candidate); }}
                                                        sx={{
                                                            color: "#7B3AB7",
                                                            textTransform: "none",
                                                            fontSize: "12px",
                                                            px: 1,
                                                            py: 0.5,
                                                        }}
                                                    >
                                                        View Resume
                                                    </Button>
                                                    <Button
                                                        variant="text"
                                                        onClick={(e) => { e.stopPropagation(); handleDownload(candidate.userId || candidate.id); }}
                                                        startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                                                        sx={{
                                                            color: "#7B3AB7",
                                                            textTransform: "none",
                                                            fontSize: "12px",
                                                            px: 1,
                                                            py: 0.5,
                                                        }}
                                                    >
                                                        Download
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        </Box>

                        {/* Candidate Details Placeholder */}
                        <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: 0 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    backgroundColor: "#fff", borderRadius: 2, border: "1px solid #E0E0E0",
                                    height: "100%", minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", p: 0,
                                }}
                            >
                                {selectedCandidate ? (
                                    <Box sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                            <Avatar src={selectedCandidate.avatarUrl || ""} sx={{ width: 80, height: 80, mr: 3 }} />
                                            <Box>
                                                <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
                                                    {selectedCandidate.name}
                                                </Typography>
                                                <Typography variant="subtitle1" sx={{ color: "#6B31C4", fontWeight: 500 }}>
                                                    {selectedCandidate.jobTitle || "Job title not specified"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 4 }}>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Location</Typography>
                                                <Typography variant="body1">{selectedCandidate.location || "Not specified"}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Experience</Typography>
                                                <Typography variant="body1">{selectedCandidate.experience ? `${selectedCandidate.experience} years` : "Not specified"}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Email</Typography>
                                                <Typography variant="body1">{selectedCandidate.email || "Not provided"}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Phone</Typography>
                                                <Typography variant="body1">{selectedCandidate.phone || "Not provided"}</Typography>
                                            </Box>
                                        </Box>

                                        <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2, fontSize: '16px' }}>
                                            Skills
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 4 }}>
                                            {selectedCandidate.skills && selectedCandidate.skills.length > 0 ? (
                                                selectedCandidate.skills.map((skill, index) => (
                                                    <Box key={index} sx={{ backgroundColor: '#f0e6ff', color: '#6B31C4', px: 2, py: 0.5, borderRadius: 4, fontSize: '13px', fontWeight: 500 }}>
                                                        {skill.name || skill}
                                                    </Box>
                                                ))
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">No skills listed</Typography>
                                            )}
                                        </Box>
                                        
                                        </Box>
                                        <Box sx={{ flexShrink: 0, display: 'flex', gap: 2, p: 2, borderTop: '1px solid #E0E0E0' }}>
                                            <Button variant="contained" onClick={() => handleDownload(selectedCandidate.userId || selectedCandidate.id)} sx={{ backgroundColor: '#6B31C4', '&:hover': { backgroundColor: '#5a29a5' }, textTransform: 'none', px: 4 }}>
                                                Download Resume
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: "center", maxWidth: "400px" }}>
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: 600, color: "#333", mb: 2, fontSize: "18px" }}
                                        >
                                            Select Candidates to view details
                                        </Typography>
                                        <Box
                                            sx={{
                                                mt: 3,
                                                mb: 3,
                                                opacity: 0.3,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    backgroundColor: "#E0E0E0",
                                                }}
                                            >
                                                <PersonIcon sx={{ fontSize: 40, color: "#999" }} />
                                            </Avatar>
                                            <Box sx={{ textAlign: "left", width: "100%" }}>
                                                <Box
                                                    sx={{
                                                        height: "12px",
                                                        backgroundColor: "#E0E0E0",
                                                        borderRadius: 1,
                                                        mb: 1,
                                                        width: "60%",
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        height: "12px",
                                                        backgroundColor: "#E0E0E0",
                                                        borderRadius: 1,
                                                        mb: 1,
                                                        width: "80%",
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        height: "12px",
                                                        backgroundColor: "#E0E0E0",
                                                        borderRadius: 1,
                                                        mb: 1,
                                                        width: "70%",
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: "#999", fontSize: "14px" }}>
                                            We have found 25 candidates for you. Select the candidate from the left side
                                            to view their full details
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Schedule Interview Dialog */}
            <Dialog 
                open={scheduleOpen} 
                onClose={() => setScheduleOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Schedule Interview</DialogTitle>
                <DialogContent>
                    {scheduleError && <Alert severity="error" sx={{ mb: 2 }}>{scheduleError}</Alert>}
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        <FormControl fullWidth>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Job Role *</FormLabel>
                            {selectedCandidate?._raw?.job || selectedCandidate?._raw?.jobId ? (
                                <TextField 
                                    size="small" 
                                    disabled 
                                    value={selectedCandidate.jobTitle || selectedCandidate?._raw?.job?.title || "Applied Job"}
                                    sx={{ bgcolor: '#f5f5f5' }}
                                />
                            ) : (
                                <Select 
                                    size="small" 
                                    value={scheduleForm.jobId} 
                                    onChange={e => setScheduleForm(f => ({ ...f, jobId: e.target.value }))}
                                >
                                    {jobsList.map(j => <MenuItem key={j.id} value={j.id}>{j.title}</MenuItem>)}
                                </Select>
                            )}
                        </FormControl>

                        <FormControl fullWidth>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Duration *</FormLabel>
                            <Select 
                                size="small" 
                                value={scheduleForm.durationMinutes} 
                                onChange={e => setScheduleForm(f => ({ ...f, durationMinutes: e.target.value }))}
                            >
                                {DURATION_OPTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Mode *</FormLabel>
                            <Select 
                                size="small" 
                                value={scheduleForm.mode} 
                                onChange={e => setScheduleForm(f => ({ ...f, mode: e.target.value }))}
                            >
                                <MenuItem value="online">Online / Video</MenuItem>
                                <MenuItem value="phone">Phone call</MenuItem>
                                <MenuItem value="in-person">In-person</MenuItem>
                            </Select>
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ flex: 2 }}>
                                <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, display: 'block' }}>Date *</FormLabel>
                                <TextField 
                                    fullWidth 
                                    size="small" 
                                    type="date" 
                                    value={scheduleForm.date}
                                    onChange={e => setScheduleForm(f => ({ ...f, date: e.target.value }))}
                                    InputLabelProps={{ shrink: true }} 
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, display: 'block' }}>Hour *</FormLabel>
                                <FormControl fullWidth>
                                    <Select 
                                        size="small" 
                                        value={scheduleForm.hour} 
                                        onChange={e => setScheduleForm(f => ({ ...f, hour: e.target.value }))}
                                    >
                                        {HOURS_12.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, display: 'block' }}>Minute *</FormLabel>
                                <FormControl fullWidth>
                                    <Select 
                                        size="small" 
                                        value={scheduleForm.minute} 
                                        onChange={e => setScheduleForm(f => ({ ...f, minute: e.target.value }))}
                                    >
                                        {MINUTES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, display: 'block' }}>AM/PM *</FormLabel>
                                <FormControl fullWidth>
                                    <Select 
                                        size="small" 
                                        value={scheduleForm.period} 
                                        onChange={e => setScheduleForm(f => ({ ...f, period: e.target.value }))}
                                    >
                                        <MenuItem value="AM">AM</MenuItem>
                                        <MenuItem value="PM">PM</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        <FormControl fullWidth>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Time Zone *</FormLabel>
                            <Select 
                                size="small" 
                                value={scheduleForm.timeZone} 
                                onChange={e => setScheduleForm(f => ({ ...f, timeZone: e.target.value }))}
                            >
                                {TIMEZONES.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>Personal Note *</FormLabel>
                            <TextField 
                                fullWidth 
                                multiline 
                                rows={3} 
                                size="small" 
                                placeholder="Message to candidate..."
                                value={scheduleForm.message} 
                                onChange={e => setScheduleForm(f => ({ ...f, message: e.target.value }))} 
                            />
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setScheduleOpen(false)} sx={{ textTransform: 'none', color: '#555' }}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        disableElevation 
                        onClick={handleScheduleSubmit}
                        disabled={scheduleSubmitting}
                        sx={{ 
                            textTransform: 'none', 
                            bgcolor: BRAND, 
                            borderRadius: 2,
                            '&:hover': { bgcolor: BRAND_DARK } 
                        }}
                    >
                        {scheduleSubmitting ? 'Scheduling...' : 'Schedule'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snack Notifications */}
            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity}>{snack.msg}</Alert>
            </Snackbar>

          
            {/* <AdvancedSearchFilters
                open={advancedFiltersOpen}
                onClose={() => setAdvancedFiltersOpen(false)}
                onSave={handleSaveFilters}
            /> */}
        </Box>
    );
};

export default SearchResults;
